import { EmployeeType } from 'lib/constants';
import { ICompany, ICompanyAffiliation, IEmployment, IUser } from 'types';

export type _IUser = IUser & {
  isSuperAdmin?: boolean;
  isEmailVerified?: boolean;
  userEmploymentType?: EmployeeType;
  isOnboarded?: boolean;
};

export type IRoleType = 'manager' | 'worker';
export type ISubRoleType = 'w2Employee' | 'contractor' | 'eorEmployee';

export interface IUserContext {
  user: _IUser | null;
  setUser: (user: _IUser | null) => void;
  logout: (
    redirectToSignIn?: boolean,
    revokeStytchSession?: boolean,
    preserveOnboardingDraft?: boolean,
  ) => Promise<void>;
  mutate: ({
    shouldRefreshSession,
    newCompanyId,
    shouldReturnAfterRefresh,
  }?: {
    shouldRefreshSession?: boolean;
    newCompanyId?: string;
    shouldReturnAfterRefresh?: boolean;
  }) => Promise<_IUser | null>;
  isLoading: boolean;
  isLoggingOut: boolean;
  affiliationsByCompany?: Record<string, ICompanyAffiliation>;
  currentTeamId: string | null;
  setCurrentTeamId: (teamId: string | null) => void;
  currentContractId: string | null;
  setCurrentContractId: (contractId: string | null) => void;
  currentContract: IEmployment | null;
  setCurrentContract: (contract: IEmployment | null) => void;
  currentCompany: ICompany | null;
  setCurrentCompany: (company: ICompany | null) => void;
  roleType: IRoleType | null;
  setRoleType: (roleType: IRoleType | null) => void;
  subRoleType: ISubRoleType | null;
  setSubRoleType: (subRoleType: ISubRoleType | null) => void;
  showAccountSwitcher: boolean;
  setShowAccountSwitcher: (show: boolean) => void;
  showAllTeamsContracts: boolean;
  isAccountSwitchingInProgress: boolean;
  setIsAccountSwitchingInProgress: (inProgress: boolean) => void;
  isTOTPSetupInProgress?: boolean;
  setTOTPSetupStatus?: (inProgress: boolean) => void;
  canSkip2FA?: boolean;
  setCanSkip2FA?: (canSkip: boolean) => void;
  is2FAVerified?: boolean;
  setIs2FAVerified?: (isVerified: boolean) => void;
  isUserProfileReady?: boolean;
  setIsUserProfileReady?: (isReady: boolean) => void;
  hasNoAffiliations?: boolean;
  hasAffiliationsError?: boolean;
}

// define action types
export const actionTypes = {
  SET_USER_PROFILE_DATA: 'SET_USER_PROFILE_DATA',
  SET_USER: 'SET_USER',
  SET_LOADING: 'SET_LOADING',
  SET_LOGGING_OUT: 'SET_LOGGING_OUT',
  SET_ACCOUNT_SWITCHING: 'SET_ACCOUNT_SWITCHING',
  SET_AFFILIATIONS: 'SET_AFFILIATIONS',
  SET_CURRENT_TEAM_ID: 'SET_CURRENT_TEAM_ID',
  SET_CURRENT_CONTRACT_ID: 'SET_CURRENT_CONTRACT_ID',
  SET_CURRENT_COMPANY: 'SET_CURRENT_COMPANY',
  SET_CURRENT_CONTRACT: 'SET_CURRENT_CONTRACT',
  SET_ROLE_TYPE: 'SET_ROLE_TYPE',
  SET_SUB_ROLE_TYPE: 'SET_SUB_ROLE_TYPE',
  SET_SHOW_ACCOUNT_SWITCHER: 'SET_SHOW_ACCOUNT_SWITCHER',
  SET_OTP_DATA: 'SET_OTP_DATA',
  RESET_STATE: 'RESET_STATE',
  SET_OTP_DATA_AND_LOADING: 'SET_OTP_DATA_AND_LOADING',
  SET_LOADING_AND_LOGGING_OUT: 'SET_LOADING_AND_LOGGING_OUT',
  SET_TOTP_SETUP_STATUS: 'SET_TOTP_SETUP_STATUS',
  SET_CAN_SKIP_2FA: 'SET_CAN_SKIP_2FA',
  SET_IS_2FA_VERIFIED: 'SET_IS_2FA_VERIFIED',
  SET_USER_PROFILE_READY: 'SET_USER_PROFILE_READY',
  SET_HAS_NO_AFFILIATIONS: 'SET_HAS_NO_AFFILIATIONS',
  SET_HAS_AFFILIATIONS_ERROR: 'SET_HAS_AFFILIATIONS_ERROR',
} as const; // <-- This enforces string literal types

export type ActionType =
  | { type: typeof actionTypes.SET_USER_PROFILE_DATA; payload: Partial<UserState> }
  | { type: typeof actionTypes.SET_USER; payload: _IUser | null }
  | { type: typeof actionTypes.SET_LOADING; payload: boolean }
  | { type: typeof actionTypes.SET_LOGGING_OUT; payload: boolean }
  | { type: typeof actionTypes.SET_ACCOUNT_SWITCHING; payload: boolean }
  | { type: typeof actionTypes.SET_AFFILIATIONS; payload: Record<string, ICompanyAffiliation> | undefined }
  | { type: typeof actionTypes.SET_CURRENT_TEAM_ID; payload: string | null }
  | { type: typeof actionTypes.SET_CURRENT_CONTRACT_ID; payload: string | null }
  | { type: typeof actionTypes.SET_CURRENT_COMPANY; payload: ICompany | null }
  | { type: typeof actionTypes.SET_CURRENT_CONTRACT; payload: IEmployment | null }
  | { type: typeof actionTypes.SET_ROLE_TYPE; payload: IRoleType | null }
  | { type: typeof actionTypes.SET_SUB_ROLE_TYPE; payload: ISubRoleType | null }
  | { type: typeof actionTypes.SET_SHOW_ACCOUNT_SWITCHER; payload: boolean }
  | { type: typeof actionTypes.SET_OTP_DATA; payload: Partial<OTPState> }
  | { type: typeof actionTypes.RESET_STATE }
  | { type: typeof actionTypes.SET_OTP_DATA_AND_LOADING; payload: { otpData: Partial<OTPState>; isLoading: boolean } }
  | { type: typeof actionTypes.SET_LOADING_AND_LOGGING_OUT; payload: { isLoading: boolean; isLoggingOut: boolean } }
  | { type: typeof actionTypes.SET_TOTP_SETUP_STATUS; payload: boolean }
  | { type: typeof actionTypes.SET_CAN_SKIP_2FA; payload: boolean }
  | { type: typeof actionTypes.SET_IS_2FA_VERIFIED; payload: boolean }
  | { type: typeof actionTypes.SET_USER_PROFILE_READY; payload: boolean }
  | { type: typeof actionTypes.SET_HAS_NO_AFFILIATIONS; payload: boolean }
  | { type: typeof actionTypes.SET_HAS_AFFILIATIONS_ERROR; payload: boolean };

export interface OTPState {
  showOTPModal: boolean;
  methodIDForOTP: string;
  emailForOTP: string;
  mfaType: string;
}
export interface UserState {
  user: _IUser | null;
  isLoading: boolean;
  isLoggingOut: boolean;
  isAccountSwitchingInProgress: boolean;
  isTOTPSetupInProgress?: boolean;
  affiliationsByCompany?: Record<string, ICompanyAffiliation>;
  currentTeamId: string | null;
  currentContractId: string | null;
  currentCompany: ICompany | null;
  currentContract: IEmployment | null;
  roleType: IRoleType | null;
  subRoleType: ISubRoleType | null;
  showAccountSwitcher: boolean;
  otpData: OTPState;
  canSkip2FA?: boolean;
  is2FAVerified?: boolean;
  isUserProfileReady?: boolean;
  hasNoAffiliations?: boolean;
  hasAffiliationsError?: boolean;
}
